import React, { useState, useRef } from 'react';
import PageInstructions from "@/components/PageInstructions";
import { Calculator, TrendingUp, Search, HelpCircle, Lightbulb, Trash2, Share2, Download } from 'lucide-react';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

interface GrupoInfo {
  nome: string;
  dezenas: string[];
}

interface GrupoEncontrado extends GrupoInfo {
  numero: number;
}

interface PalpitesResult {
  milharOriginal: string;
  dezenaExtraida: string;
  valorDezena: number;
  grupoResultado: GrupoEncontrado;
  grupoCorrespondente: GrupoInfo;
  usarApenasGrupoResultado: boolean;
  tipoCalculo: string;
  milhares: string[];
  centenas: string[];
}

const AcharCentenaDia = () => {
  const [milharResultado, setMilharResultado] = useState('');
  const [palpites, setPalpites] = useState<PalpitesResult | null>(null);
  const [mostrarTutorial, setMostrarTutorial] = useState(false);
  const resultadosRef = useRef<HTMLDivElement>(null);

  // Grupos do Jogo do Bicho (1-25)
  const grupos: Record<number, GrupoInfo> = {
    1: { nome: 'Avestruz', dezenas: ['01', '02', '03', '04'] },
    2: { nome: 'Águia', dezenas: ['05', '06', '07', '08'] },
    3: { nome: 'Burro', dezenas: ['09', '10', '11', '12'] },
    4: { nome: 'Borboleta', dezenas: ['13', '14', '15', '16'] },
    5: { nome: 'Cachorro', dezenas: ['17', '18', '19', '20'] },
    6: { nome: 'Cabra', dezenas: ['21', '22', '23', '24'] },
    7: { nome: 'Carneiro', dezenas: ['25', '26', '27', '28'] },
    8: { nome: 'Camelo', dezenas: ['29', '30', '31', '32'] },
    9: { nome: 'Cobra', dezenas: ['33', '34', '35', '36'] },
    10: { nome: 'Coelho', dezenas: ['37', '38', '39', '40'] },
    11: { nome: 'Cavalo', dezenas: ['41', '42', '43', '44'] },
    12: { nome: 'Elefante', dezenas: ['45', '46', '47', '48'] },
    13: { nome: 'Galo', dezenas: ['49', '50', '51', '52'] },
    14: { nome: 'Gato', dezenas: ['53', '54', '55', '56'] },
    15: { nome: 'Jacaré', dezenas: ['57', '58', '59', '60'] },
    16: { nome: 'Leão', dezenas: ['61', '62', '63', '64'] },
    17: { nome: 'Macaco', dezenas: ['65', '66', '67', '68'] },
    18: { nome: 'Porco', dezenas: ['69', '70', '71', '72'] },
    19: { nome: 'Pavão', dezenas: ['73', '74', '75', '76'] },
    20: { nome: 'Peru', dezenas: ['77', '78', '79', '80'] },
    21: { nome: 'Touro', dezenas: ['81', '82', '83', '84'] },
    22: { nome: 'Tigre', dezenas: ['85', '86', '87', '88'] },
    23: { nome: 'Urso', dezenas: ['89', '90', '91', '92'] },
    24: { nome: 'Veado', dezenas: ['93', '94', '95', '96'] },
    25: { nome: 'Vaca', dezenas: ['97', '98', '99', '00'] }
  };

  // Encontrar grupo pela dezena
  const encontrarGrupo = (dezena: string): GrupoEncontrado | null => {
    for (const [grupo, info] of Object.entries(grupos)) {
      if (info.dezenas.includes(dezena)) {
        return { numero: parseInt(grupo), ...info };
      }
    }
    return null;
  };

  const calcularPalpites = () => {
    if (milharResultado.length !== 4) {
      toast.error('Por favor, insira uma milhar válida (4 dígitos)');
      return;
    }

    // Extrair a dezena (últimos 2 dígitos)
    const dezena = milharResultado.slice(-2);
    const valorDezena = parseInt(dezena);
    
    // Encontrar o grupo da dezena que saiu
    const grupoResultado = encontrarGrupo(dezena);
    
    if (!grupoResultado) {
      toast.error('Dezena não encontrada nos grupos');
      return;
    }

    let grupoCorrespondente: GrupoInfo | null = null;
    let usarApenasGrupoResultado = false;
    let tipoCalculo = '';

    // REGRA: Se a dezena está entre 01 e 25, o grupo correspondente é o grupo com esse número
    if (valorDezena >= 1 && valorDezena <= 25) {
      grupoCorrespondente = grupos[valorDezena];
      tipoCalculo = `Dezena ${dezena} (entre 01-25) → Grupo correspondente é o Grupo ${valorDezena}`;
    } else {
      // Se dezena for maior que 25 (26-99 ou 00), usa apenas o grupo que saiu
      grupoCorrespondente = grupoResultado;
      usarApenasGrupoResultado = true;
      tipoCalculo = `Dezena ${dezena} (maior que 25) → Usa apenas o ${grupoResultado.nome}`;
    }

    const milhares: string[] = [];
    const centenas: string[] = [];

    if (usarApenasGrupoResultado) {
      // Combinar o grupo consigo mesmo
      grupoResultado.dezenas.forEach(dez1 => {
        grupoResultado.dezenas.forEach(dez2 => {
          milhares.push(dez1 + dez2);
          if (dez1 !== dez2) {
            milhares.push(dez2 + dez1);
          }
        });
      });
      
      // Centenas apenas do grupo resultado
      grupoResultado.dezenas.forEach(dez => {
        if (!centenas.includes(dez)) {
          centenas.push(dez);
        }
      });
    } else {
      // Combinar dezenas do grupo que saiu com dezenas do grupo correspondente
      grupoResultado.dezenas.forEach(dez1 => {
        grupoCorrespondente!.dezenas.forEach(dez2 => {
          milhares.push(dez1 + dez2);
          milhares.push(dez2 + dez1);
        });
      });

      // Formar centenas de ambos os grupos
      [...grupoResultado.dezenas, ...grupoCorrespondente!.dezenas].forEach(dez => {
        if (!centenas.includes(dez)) {
          centenas.push(dez);
        }
      });
    }

    setPalpites({
      milharOriginal: milharResultado,
      dezenaExtraida: dezena,
      valorDezena: valorDezena,
      grupoResultado: grupoResultado,
      grupoCorrespondente: grupoCorrespondente!,
      usarApenasGrupoResultado: usarApenasGrupoResultado,
      tipoCalculo: tipoCalculo,
      milhares: [...new Set(milhares)].sort(),
      centenas: centenas.sort()
    });

    toast.success('Palpites gerados com sucesso!');
  };

  const limparResultados = () => {
    setMilharResultado('');
    setPalpites(null);
  };

  const gerarTexto = () => {
    if (!palpites) return '';
    
    let texto = '═══════════════════════════════════════\n';
    texto += '   ACHAR CENTENA DO DIA - RESULTADO\n';
    texto += '═══════════════════════════════════════\n\n';
    texto += `📊 ANÁLISE DO RESULTADO\n`;
    texto += `─────────────────────────────────────\n`;
    texto += `Milhar Original: ${palpites.milharOriginal}\n`;
    texto += `Dezena Extraída: ${palpites.dezenaExtraida}\n`;
    texto += `Grupo da Dezena: ${palpites.grupoResultado.nome} (Grupo ${palpites.grupoResultado.numero})\n`;
    texto += `Dezenas: ${palpites.grupoResultado.dezenas.join(', ')}\n\n`;
    texto += `Grupo Correspondente: ${palpites.grupoCorrespondente.nome}\n`;
    texto += `Dezenas: ${palpites.grupoCorrespondente.dezenas.join(', ')}\n\n`;
    texto += `Método: ${palpites.tipoCalculo}\n\n`;
    
    texto += `🎯 CENTENAS DO DIA (${palpites.centenas.length})\n`;
    texto += `─────────────────────────────────────\n`;
    for (let i = 0; i < palpites.centenas.length; i += 8) {
      texto += palpites.centenas.slice(i, i + 8).join('  ') + '\n';
    }
    
    texto += `\n🎲 MILHARES GERADAS (${palpites.milhares.length})\n`;
    texto += `─────────────────────────────────────\n`;
    for (let i = 0; i < palpites.milhares.length; i += 8) {
      texto += palpites.milhares.slice(i, i + 8).join('  ') + '\n';
    }
    
    texto += '\n═══════════════════════════════════════\n';
    texto += '⚠️ Apenas para fins de entretenimento\n';
    texto += '   Jogue com responsabilidade\n';
    texto += '═══════════════════════════════════════';
    
    return texto;
  };

  const baixarTxt = () => {
    if (!palpites) return;
    
    const texto = gerarTexto();
    const blob = new Blob([texto], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `centena-do-dia-${palpites.milharOriginal}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Arquivo TXT baixado com sucesso!');
  };

  const baixarImagem = (blob: Blob) => {
    if (!palpites) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `centena-dia-${palpites.milharOriginal}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const compartilharImagem = async () => {
    if (!palpites || !resultadosRef.current) return;
    
    try {
      const canvas = await html2canvas(resultadosRef.current, {
        backgroundColor: '#064e3b',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error('Erro ao gerar imagem');
          return;
        }
        
        if (navigator.share && navigator.canShare) {
          try {
            const file = new File([blob], `centena-dia-${palpites.milharOriginal}.png`, { type: 'image/png' });
            await navigator.share({
              files: [file],
              title: 'Achar Centena do Dia',
              text: `Palpites baseados na milhar ${palpites.milharOriginal}`
            });
          } catch {
            baixarImagem(blob);
            toast.success('Imagem baixada com sucesso!');
          }
        } else {
          baixarImagem(blob);
          toast.success('Imagem baixada com sucesso!');
        }
      });
    } catch {
      toast.error('Erro ao gerar imagem. Tente novamente.');
    }
  };

  const exemplosTutorial = [
    {
      milhar: '4523',
      dezena: '23',
      valor: 23,
      grupo1: 'Cabra (Grupo 6)',
      grupo2: 'Urso (Grupo 23)',
      explicacao: 'Dezena 23 = Cabra (Grupo 6). Como 23 está entre 01-25, o grupo correspondente é o Grupo 23 (Urso)',
      dezenas1: '21, 22, 23, 24',
      dezenas2: '89, 90, 91, 92',
      regra: '✅ Dezena 01-25: Combina grupo da dezena + Grupo correspondente ao número'
    },
    {
      milhar: '3621',
      dezena: '21',
      valor: 21,
      grupo1: 'Cabra (Grupo 6)',
      grupo2: 'Touro (Grupo 21)',
      explicacao: 'Dezena 21 = Cabra (Grupo 6). Como 21 está entre 01-25, o grupo correspondente é o Grupo 21 (Touro)',
      dezenas1: '21, 22, 23, 24',
      dezenas2: '81, 82, 83, 84',
      regra: '✅ Dezena 01-25: Combina grupo da dezena + Grupo correspondente ao número'
    },
    {
      milhar: '7805',
      dezena: '05',
      valor: 5,
      grupo1: 'Águia (Grupo 2)',
      grupo2: 'Cachorro (Grupo 5)',
      explicacao: 'Dezena 05 = Águia (Grupo 2). Como 05 está entre 01-25, o grupo correspondente é o Grupo 5 (Cachorro)',
      dezenas1: '05, 06, 07, 08',
      dezenas2: '17, 18, 19, 20',
      regra: '✅ Dezena 01-25: Combina grupo da dezena + Grupo correspondente ao número'
    },
    {
      milhar: '4597',
      dezena: '97',
      valor: 97,
      grupo1: 'Vaca (Grupo 25)',
      grupo2: 'Vaca (Grupo 25)',
      explicacao: 'Dezena 97 = Vaca (Grupo 25). Como 97 > 25, usa apenas as dezenas do próprio grupo (Vaca)',
      dezenas1: '97, 98, 99, 00',
      dezenas2: '97, 98, 99, 00',
      regra: '⚠️ Dezena > 25: Usa apenas o grupo que saiu'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-800 via-emerald-700 to-green-800 p-4 sm:p-6 rounded-xl">
      <div className="max-w-6xl mx-auto">
        <PageInstructions
          title="📖 Como usar o Achar Centena do Dia"
          steps={[
            "Digite o resultado do 1º prêmio do último sorteio no campo indicado",
            "O sistema identifica automaticamente o grupo e o bicho correspondente",
            "Clique em 'Calcular' para gerar as centenas do dia",
            "Veja as centenas sugeridas e os grupos relacionados",
            "Use os botões para compartilhar ou baixar o resultado"
          ]}
        />
        {/* Cabeçalho */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Search className="w-8 h-8 sm:w-10 sm:h-10 text-yellow-400" />
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
              ACHAR CENTENA DO DIA
            </h1>
          </div>
          <p className="text-emerald-200 text-sm sm:text-base md:text-lg mb-4">
            Método: Grupo da Dezena + Grupo pelo Número da Dezena (01-25)
          </p>
          
          {/* Botão Tutorial */}
          <button
            onClick={() => setMostrarTutorial(!mostrarTutorial)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg flex items-center gap-2 mx-auto transition-all"
          >
            <HelpCircle className="w-5 h-5" />
            {mostrarTutorial ? 'Ocultar Tutorial' : 'Ver Tutorial de Uso'}
          </button>
        </div>

        {/* Tutorial */}
        {mostrarTutorial && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 mb-6 border border-white/20">
            <h2 className="text-xl sm:text-2xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6" />
              Como Usar o Método "ACHAR CENTENA DO DIA"
            </h2>
            
            <div className="space-y-4 text-white">
              <div className="bg-emerald-800/50 rounded-lg p-4">
                <h3 className="font-bold text-base sm:text-lg mb-2 text-yellow-300">📌 Passo 1: Digite o Resultado</h3>
                <p className="text-emerald-100 text-sm sm:text-base">Digite a milhar do último resultado do 1º prêmio (4 dígitos). Exemplo: <strong>4523</strong></p>
              </div>

              <div className="bg-emerald-800/50 rounded-lg p-4">
                <h3 className="font-bold text-base sm:text-lg mb-2 text-yellow-300">📌 Passo 2: Sistema Extrai a Dezena</h3>
                <p className="text-emerald-100 text-sm sm:text-base">O sistema pega os 2 últimos dígitos. Exemplo: de 4523 → extrai <strong>23</strong></p>
              </div>

              <div className="bg-emerald-800/50 rounded-lg p-4">
                <h3 className="font-bold text-base sm:text-lg mb-2 text-yellow-300">📌 Passo 3: Identifica o Grupo da Dezena</h3>
                <p className="text-emerald-100 text-sm sm:text-base">
                  Exemplo: dezena <strong>23</strong> pertence à <strong>Cabra (Grupo 6)</strong>
                </p>
              </div>

              <div className="bg-blue-800/50 rounded-lg p-4">
                <h3 className="font-bold text-base sm:text-lg mb-2 text-yellow-300">📌 Passo 4: Define o Grupo Correspondente</h3>
                <div className="space-y-2 text-blue-100 text-sm sm:text-base">
                  <p className="font-semibold text-yellow-200">REGRA PRINCIPAL:</p>
                  <p>🔹 <strong>Se a dezena for 01 a 25:</strong><br/>
                  O grupo correspondente é o <strong>Grupo com esse número</strong></p>
                  <p className="text-xs sm:text-sm ml-4 bg-blue-900/50 p-3 rounded mt-2">
                    <strong>Exemplo da milhar 4523:</strong><br/>
                    • Dezena extraída: <strong>23</strong><br/>
                    • Grupo da dezena: Cabra (Grupo 6) → dezenas 21, 22, 23, 24<br/>
                    • Grupo correspondente: <strong>Grupo 23</strong> (Urso) → dezenas 89, 90, 91, 92<br/>
                    • <strong>Combina:</strong> dezenas do Grupo 6 com dezenas do Grupo 23
                  </p>
                  
                  <p className="mt-4">🔸 <strong>Se a dezena for 26 a 99 ou 00:</strong><br/>
                  Usa apenas as dezenas do próprio grupo que saiu</p>
                  <p className="text-xs sm:text-sm ml-4 bg-blue-900/50 p-3 rounded mt-2">
                    <strong>Exemplo da milhar 4597:</strong><br/>
                    • Dezena extraída: <strong>97</strong><br/>
                    • Grupo da dezena: Vaca (Grupo 25) → dezenas 97, 98, 99, 00<br/>
                    • Como 97 &gt; 25: usa apenas dezenas da Vaca combinadas entre si
                  </p>
                </div>
              </div>

              <div className="bg-emerald-800/50 rounded-lg p-4">
                <h3 className="font-bold text-base sm:text-lg mb-2 text-yellow-300">📌 Passo 5: Forma as Combinações</h3>
                <p className="text-emerald-100 text-sm sm:text-base">
                  • <strong>Centenas:</strong> todas as dezenas dos 2 grupos identificados<br/>
                  • <strong>Milhares:</strong> combina as dezenas do grupo da dezena com as dezenas do grupo correspondente<br/>
                  • Exemplo: 21+89, 21+90, 21+91, 21+92, 22+89, 22+90, etc.
                </p>
              </div>

              <div className="bg-purple-800/50 rounded-lg p-4">
                <h3 className="font-bold text-lg sm:text-xl mb-3 text-yellow-300">💡 Exemplos Práticos:</h3>
                <div className="space-y-3">
                  {exemplosTutorial.map((ex, idx) => (
                    <div key={idx} className="bg-purple-900/50 rounded p-3 sm:p-4 border-l-4 border-yellow-400">
                      <p className="font-bold text-yellow-200 text-base sm:text-lg">Milhar: {ex.milhar} → Dezena: {ex.dezena}</p>
                      <p className="text-purple-100 text-xs sm:text-sm mt-2">{ex.explicacao}</p>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs sm:text-sm">
                        <div className="bg-purple-800/50 p-2 rounded">
                          <p className="font-semibold text-purple-200">{ex.grupo1}</p>
                          <p className="text-purple-300">Dezenas: {ex.dezenas1}</p>
                        </div>
                        <div className="bg-purple-800/50 p-2 rounded">
                          <p className="font-semibold text-purple-200">{ex.grupo2}</p>
                          <p className="text-purple-300">Dezenas: {ex.dezenas2}</p>
                        </div>
                      </div>
                      <p className="text-purple-300 text-xs mt-2 italic font-semibold">{ex.regra}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-red-800/50 rounded-lg p-4">
                <h3 className="font-bold text-base sm:text-lg mb-2 text-yellow-300">⚠️ Resumo da Regra:</h3>
                <p className="text-red-100 text-sm sm:text-base">
                  • <strong>Dezena 01-25:</strong> Combina grupo da dezena + Grupo correspondente ao número da dezena<br/>
                  • <strong>Dezena 26-99/00:</strong> Usa apenas o grupo que saiu combinado consigo mesmo<br/>
                  • Gera automaticamente todas as combinações possíveis de milhares e centenas
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Card de Entrada */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 md:p-8 mb-6 border border-white/20">
          <div className="mb-6">
            <label className="block text-white text-sm sm:text-base md:text-lg font-semibold mb-3">
              Último Resultado do 1º Prêmio (Milhar):
            </label>
            <input
              type="text"
              maxLength={4}
              value={milharResultado}
              onChange={(e) => setMilharResultado(e.target.value.replace(/\D/g, ''))}
              placeholder="Ex: 4523"
              className="w-full px-4 sm:px-6 py-3 sm:py-4 text-xl sm:text-2xl md:text-3xl font-bold text-center bg-white/20 border-2 border-yellow-400 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-4 focus:ring-yellow-400/50"
            />
          </div>

          <button
            onClick={calcularPalpites}
            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-emerald-900 font-bold py-3 sm:py-4 px-4 sm:px-8 rounded-xl text-base sm:text-lg md:text-xl hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 sm:gap-3"
          >
            <Calculator className="w-5 h-5 sm:w-6 sm:h-6" />
            Gerar Palpites
          </button>

          {palpites && (
            <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
              <button
                onClick={limparResultados}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 sm:py-3 px-2 sm:px-4 rounded-lg flex items-center justify-center gap-1 sm:gap-2 transition-all text-xs sm:text-sm"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">Limpar</span>
              </button>
              
              <button
                onClick={compartilharImagem}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 sm:py-3 px-2 sm:px-4 rounded-lg flex items-center justify-center gap-1 sm:gap-2 transition-all text-xs sm:text-sm"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Imagem</span>
              </button>
              
              <button
                onClick={baixarTxt}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 sm:py-3 px-2 sm:px-4 rounded-lg flex items-center justify-center gap-1 sm:gap-2 transition-all text-xs sm:text-sm"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">TXT</span>
              </button>
            </div>
          )}
        </div>

        {/* Resultados */}
        {palpites && (
          <div ref={resultadosRef} className="space-y-4 sm:space-y-6">
            {/* Informações do Cálculo */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-white/20">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6" />
                Análise do Resultado
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-emerald-800/50 rounded-lg p-3 sm:p-4">
                  <p className="text-emerald-200 text-xs sm:text-sm mb-1">Milhar Original:</p>
                  <p className="text-white text-xl sm:text-2xl md:text-3xl font-bold">{palpites.milharOriginal}</p>
                </div>
                
                <div className="bg-emerald-800/50 rounded-lg p-3 sm:p-4">
                  <p className="text-emerald-200 text-xs sm:text-sm mb-1">Dezena Extraída:</p>
                  <p className="text-white text-xl sm:text-2xl md:text-3xl font-bold">{palpites.dezenaExtraida}</p>
                </div>
                
                <div className="bg-blue-800/50 rounded-lg p-3 sm:p-4">
                  <p className="text-blue-200 text-xs sm:text-sm mb-1">Grupo da Dezena:</p>
                  <p className="text-white text-base sm:text-lg md:text-xl font-bold">
                    {palpites.grupoResultado.nome} (Grupo {palpites.grupoResultado.numero})
                  </p>
                  <p className="text-blue-300 text-xs mt-1">Dezenas: {palpites.grupoResultado.dezenas.join(', ')}</p>
                </div>
                
                <div className="bg-purple-800/50 rounded-lg p-3 sm:p-4">
                  <p className="text-purple-200 text-xs sm:text-sm mb-1">Grupo Correspondente:</p>
                  <p className="text-white text-base sm:text-lg md:text-xl font-bold">
                    {palpites.grupoCorrespondente.nome}
                  </p>
                  <p className="text-purple-300 text-xs mt-1">Dezenas: {palpites.grupoCorrespondente.dezenas.join(', ')}</p>
                </div>
              </div>

              <div className="mt-4 bg-yellow-900/30 border-l-4 border-yellow-400 p-3 sm:p-4 rounded">
                <p className="text-yellow-100 text-xs sm:text-sm">
                  <strong>Método aplicado:</strong> {palpites.tipoCalculo}
                </p>
              </div>
            </div>

            {/* Centenas */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-white/20">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-yellow-400 mb-4">
                🎯 Centenas do Dia ({palpites.centenas.length})
              </h2>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 sm:gap-3">
                {palpites.centenas.map((centena, idx) => (
                  <div
                    key={idx}
                    className="bg-gradient-to-br from-yellow-400 to-yellow-600 text-emerald-900 font-bold text-base sm:text-lg md:text-xl p-2 sm:p-3 md:p-4 rounded-lg text-center shadow-lg hover:scale-110 transition-transform"
                  >
                    {centena}
                  </div>
                ))}
              </div>
            </div>

            {/* Milhares */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-white/20">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-yellow-400 mb-4">
                🎲 Milhares Geradas ({palpites.milhares.length})
              </h2>
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2 sm:gap-3 max-h-80 sm:max-h-96 overflow-y-auto">
                {palpites.milhares.map((milhar, idx) => (
                  <div
                    key={idx}
                    className="bg-gradient-to-br from-emerald-400 to-emerald-600 text-white font-bold text-sm sm:text-base md:text-lg p-2 sm:p-3 rounded-lg text-center shadow-lg hover:scale-105 transition-transform"
                  >
                    {milhar}
                  </div>
                ))}
              </div>
            </div>

            {/* Grupos Utilizados */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 border border-white/20">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-yellow-400 mb-4">
                📋 Grupos Utilizados na Combinação
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-blue-800/50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-2 sm:mb-3">
                    {palpites.grupoResultado.nome} (Grupo {palpites.grupoResultado.numero})
                  </h3>
                  <p className="text-blue-200 text-xs sm:text-sm mb-2">Grupo da dezena {palpites.dezenaExtraida} que saiu</p>
                  <div className="flex flex-wrap gap-2">
                    {palpites.grupoResultado.dezenas.map((dez, idx) => (
                      <span
                        key={idx}
                        className="bg-blue-600 text-white px-2 sm:px-3 py-1 rounded-lg font-semibold text-sm"
                      >
                        {dez}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="bg-purple-800/50 rounded-lg p-3 sm:p-4">
                  <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-2 sm:mb-3">
                    {palpites.grupoCorrespondente.nome}
                  </h3>
                  <p className="text-purple-200 text-xs sm:text-sm mb-2">
                    {palpites.usarApenasGrupoResultado 
                      ? `Dezena ${palpites.dezenaExtraida} > 25, usa mesmo grupo`
                      : `Grupo correspondente ao valor ${palpites.valorDezena}`
                    }
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {palpites.grupoCorrespondente.dezenas.map((dez, idx) => (
                      <span
                        key={idx}
                        className="bg-purple-600 text-white px-2 sm:px-3 py-1 rounded-lg font-semibold text-sm"
                      >
                        {dez}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rodapé */}
        <div className="mt-6 sm:mt-8 text-center">
          <p className="text-emerald-200 text-xs sm:text-sm">
            ⚠️ Apenas para fins de entretenimento. Jogue com responsabilidade.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcharCentenaDia;
